/**
 * Calculate manual rating from rank position using non-linear distribution
 *
 * Distribution strategy:
 * - Top tier (ranks 1-3): Compressed at 9.0-10.0 (elite players)
 * - Mid tier (ranks 4-7): Wider spread 4.5-8.0 (clear differentiation)
 * - Bottom tier (ranks 8+): Compressed at 1.0-3.5 (weak players)
 *
 * @param rank - Position in ranking (1 = best, N = worst)
 * @param totalPlayers - Total number of players being ranked
 * @returns Rating value between 1.0 and 10.0
 */
export function calculateNonLinearRating(rank: number, totalPlayers: number): number {
  const maxRating = 10.0;
  const minRating = 1.0;

  // Handle edge cases
  if (totalPlayers === 1) return maxRating;
  if (rank === 1) return maxRating;
  if (rank === totalPlayers) return minRating;

  // Normalize rank to 0-1 scale (0 = best, 1 = worst)
  const normalizedPosition = (rank - 1) / (totalPlayers - 1);

  // Apply power curve for non-linear distribution
  // Using power of 1.8 creates good compression at top/bottom
  // with spread in middle
  const curve = 1 - Math.pow(1 - normalizedPosition, 1.8);

  // Map to rating range (10.0 to 1.0)
  const rating = maxRating - (curve * (maxRating - minRating));

  // Round to 1 decimal place
  return Math.round(rating * 10) / 10;
}

/**
 * Calculate ratings for all players in a ranked list
 *
 * @param playerIds - Array of player IDs in rank order (best to worst)
 * @returns Map of player ID to calculated rating
 */
export function calculateRatingsForRankedList(playerIds: string[]): Record<string, number> {
  const ratings: Record<string, number> = {};
  const totalPlayers = playerIds.length;

  playerIds.forEach((playerId, index) => {
    const rank = index + 1; // Convert 0-based index to 1-based rank
    ratings[playerId] = calculateNonLinearRating(rank, totalPlayers);
  });

  return ratings;
}
